import {
  AfterContentChecked,
  Component,
  ViewChild,
  ElementRef,
  Input,
  OnInit,
} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { LinkItem } from '../../models/common';
import {
  AuthenticationService,
  Credentials,
  PasscodeChallenge,
  PasscodeChallengeResponse,
} from '../../services/authentication.service';
import { PasscodeComponent } from '../../components/passcode/passcode.component';
import { BrandService } from '../../services/brand.service';
import { AnalyticsService } from '../../services/analytics.service';
import { NativeBridge } from '../../services/nativeBridge';
import { ErrorService } from '../../services/error.service';
import { BrandedRouter } from '../../services/brandedRouter';
import { InstrumentationService } from '../../services/instrumentation.service';
import { CookieService } from 'ngx-cookie-service';
import { WindowRef } from '../../services/windowRef';
import { takeUntil } from 'rxjs/operators';
import { ContentService } from '../../services/content.service';
import { ThreatMetrixService } from '../../services/threat-metrix.service';
import { HttpErrorResponse } from '@angular/common/http';
import { NonSCAStoreStaffCardsService } from '../../services/strong-customer-authentication/non-SCA-store-staff-cards.service';
import { Subject, Observable } from 'rxjs';
import { LoginService } from '../../services/login.service';
import {
  LoggedOutEntitlement,
  LoggedOutEntitlementsService,
} from '../../services/logged-out-entitlements.service';
import {
  CookieConsentService,
  CookiePreferences,
} from '../../services/cookie-consent.service';
import { AssetConfigVariableService } from '../../services/asset-config-variable.service';
import {
  gtmEventTracking,
  GTMTracking,
} from '../../enums/google-analytics-tracking';
import { INonScaLoginContent } from '../../../app/models/content';

class CredentialsMessage {
  message: string;
  url: string;
  srMessage: string;
  detail: string;
  linkText: string;
}

@Component({
  selector: '[data-component=login]',
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnInit, AfterContentChecked {
  @Input() loginReady: boolean;
  @ViewChild('passcodeComponent') passcodeComponent: PasscodeComponent;

  public imageRootPath: string = '';
  private returnUrl: string = 'account-summary?tailored=true';
  private challengeVerificationToken: string;
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  public errors: Array<any> = [];
  public errorMessages: any = {
    passcode: 'Passcode is required.',
  };
  public credentialsMessage: CredentialsMessage = {
    url: '',
    message: '',
    srMessage: '',
    detail: '',
    linkText: '',
  };
  public passcodeInputs: any[] = [];
  public usernameInputHidden: boolean = false;
  public passwordInputHidden: boolean = true;
  public loginForm: FormGroup;
  public credentialsDirty: boolean = false;
  public step: number = 1;
  public forgotUsernameLink: LinkItem;
  public forgotPasswordLink: LinkItem;
  public forgotPasscodeLink: LinkItem;
  public registerLink: LinkItem;
  public formDirty: boolean = false;
  public isSubmitting: boolean = false;
  public screenreaderErrorMessages: string = '';
  public mobileLinksEnabled: boolean;
  public hasSCAEnabled: boolean;
  public brand: string;
  public googlePlayLink: string;
  public appStoreLink: string;
  public mobileAppName: string;
  public loginError = false;
  public cookiePreferences: Observable<CookiePreferences>;
  public GTMTracking = GTMTracking;
  public content: INonScaLoginContent;
  public isContentLoading = true;

  constructor(
    private activatedRoute: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private brandedRouter: BrandedRouter,
    private brandService: BrandService, // todo using brand service and brand router
    private cookieService: CookieService,
    private elementRef: ElementRef,
    public errorService: ErrorService,
    private instrumentation: InstrumentationService,
    private nativeBridge: NativeBridge,
    private analyticsService: AnalyticsService,
    private windowRef: WindowRef,
    private contentService: ContentService,
    private nonSCAStoreStaffCardsService: NonSCAStoreStaffCardsService,
    private threatMetrixService: ThreatMetrixService,
    private loginService: LoginService,
    private loggedOutEntitlementsService: LoggedOutEntitlementsService,
    public cookieConsentService: CookieConsentService,
    private assetConfigVariableService: AssetConfigVariableService
  ) {
    this.brand = this.brandService.getBrand();
    this.checkEntitlements();

    instrumentation.mark('LoginComponent constructor start');

    this.errorService.clear();

    instrumentation.mark('LoginComponent constructor end');
    instrumentation.measure(
      'LoginComponent constructor',
      'LoginComponent constructor start',
      'LoginComponent constructor end'
    );
  }

  async ngOnInit() {
    await this.loadContent();

    this.generateLinks();

    // If a user clicks login on recover username, or ends up at login by some other mechanism
    // let the native app know so it can remove the webview
    this.nativeBridge.notifyTaskCompleted();

    const event = gtmEventTracking[GTMTracking.LoginVisitPage];
    this.analyticsService.trackPageView(
      `${this.brand}/${event.url}`,
      event.title
    );
    if (this.step === 1) {
      this.analyticsService.trackPageView(
        this.brand + '/login/step1',
        'Login Step One'
      );
    }

    this.loginReady = true;
    this.activatedRoute.queryParams.subscribe((params: Params) => {
      if (
        typeof params['ReturnUrl'] !== 'undefined' &&
        params['ReturnUrl'].length > 0
      )
        this.returnUrl = params['ReturnUrl'];
    });

    this.cookiePreferences = this.cookieConsentService.cookiePreferences;

    this.initForm();

    this.imageRootPath =
      await this.assetConfigVariableService.getImageRootPath();
  }

  ngAfterContentChecked() {
    if (this.loginForm) {
      this.screenreaderErrorMessages = this.getScreenreaderErrorMessages();
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public trackGAEvents(action: GTMTracking) {
    this.analyticsService.trackCtaActions(action);
  }

  public changePasswordInputVisibility(isHidden: boolean): void {
    this.trackGAEvents(GTMTracking.LoginRevelPassword);
    this.passwordInputHidden = isHidden;
  }

  public getCredentialsFormGroup() {
    return this.loginForm.controls['userCredentials'] as FormGroup;
  }

  public credentialFieldHasError(field: any) {
    const credentials = this.getCredentialsFormGroup();
    return (
      !credentials.controls[field].valid &&
      (this.credentialsDirty ||
        credentials.controls[field].dirty ||
        credentials.controls[field].touched)
    );
  }

  public credentialFieldHasRequiredError(field: any) {
    return this.getCredentialsFormGroup().controls[field].errors?.required;
  }

  public submitLogin() {
    this.formDirty = true;

    if (this.step === 1) this.submitStepOne();
    else if (this.step === 2) this.submitStepTwo();
  }

  private async checkEntitlements() {
    this.mobileLinksEnabled =
      await this.loggedOutEntitlementsService.hasEntitlement(
        LoggedOutEntitlement.mobileAppBanner
      );
    this.hasSCAEnabled = await this.loggedOutEntitlementsService.hasEntitlement(
      LoggedOutEntitlement.scaLogin
    );
  }

  private async loadContent(): Promise<void> {
    try {
      const { global, apps, nonScaLogin } = await this.contentService.getData();

      this.googlePlayLink = global.googlePlayStoreAppLink;
      this.appStoreLink = global.itunesStoreAppLink;
      this.mobileAppName = apps && apps.name ? apps.name : this.brand;

      this.content = nonScaLogin;
    } finally {
      this.isContentLoading = false;
    }
  }

  public get mobileAppBannerDescription() {
    return this.content.mobileAppBanner.description.replace(
      /{{ mobileAppName }}/g,
      this.mobileAppName
    );
  }

  private getScreenreaderErrorMessages(): string {
    this.processErrorMessages();
    if (
      !(
        this.getCredentialsFormGroup().dirty ||
        this.getCredentialsFormGroup().touched ||
        this.formDirty
      ) &&
      !this.getCredentialsFormGroup().valid
    ) {
      return null;
    }

    return this.errorService.getScreenreaderErrors(this.errors);
  }

  private async initForm() {
    const username: string = await this.getUsername();

    this.loginForm = new FormGroup({
      userCredentials: new FormGroup({
        userName: new FormControl(username, [Validators.required]),
        password: new FormControl('', [Validators.required]),
      }),
      rememberMe: new FormControl(false),
      passcode: new FormControl(),
    });
  }

  private getUsername(): Promise<string> {
    return new Promise((resolve) => {
      let username: string;
      try {
        username = this.cookieService.get(this.brand + 'userName');
      } catch (exception) {
        username = '';
      }
      if (username) {
        resolve(username);
      } else {
        this.nonSCAStoreStaffCardsService.username
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((u) => resolve(u));
      }
    });
  }

  private generateLinks() {
    this.forgotUsernameLink = new LinkItem(
      this.content.form.usernameInput.forgottenLinkLabel,
      this.brandedRouter.getBrandedLink('/recovery/retrieve-username'),
      false,
      'btnRetrieveUsername'
    );
    this.forgotPasswordLink = new LinkItem(
      this.content.form.passwordInput.forgottenLinkLabel,
      this.brandedRouter.getBrandedLink('/recovery/reset-password'),
      false,
      'btnResetPassword'
    );
    this.forgotPasscodeLink = new LinkItem(
      'Forgotten passcode?',
      this.brandedRouter.getBrandedLink('/recovery/reset-passcode'),
      false,
      'btnResetPasscode'
    );

    this.registerLink = new LinkItem(
      this.content.journey.submitUsername.signUpSuggestion.linkLabel,
      this.brandedRouter.getBrandedLink('/register'),
      false
    );
  }

  private gotoStep2() {
    const event = gtmEventTracking[GTMTracking.LoginVisitPagePasscode];
    this.analyticsService.trackPageView(
      `${this.brand}/${event.url}`,
      event.title
    );
    this.analyticsService.trackPageView(
      this.brand + '/login/step2',
      'Login Step Two'
    );

    this.credentialsMessage = {
      url: '',
      message: '',
      srMessage: '',
      detail: '',
      linkText: '',
    };

    this.formDirty = false;
    this.step = 2;
    this.scrollToTop();
    this.setPasscodeFocus(50);
  }

  private scrollToTop() {
    const yPos = this.elementRef.nativeElement.offsetTop;
    this.windowRef.scrollTo(0, yPos);
  }

  private setPasscodeFocus(delay: number) {
    const that = this;
    setTimeout(() => {
      if (that.passcodeComponent == null) return;

      that.passcodeComponent.setFocus();
    }, delay);
  }

  private submitStepOne() {
    const that = this;

    this.loginError = false;

    this.credentialsDirty = true;
    if (this.credentialFieldHasRequiredError('password')) {
      this.analyticsService.pushToDataLayer(
        gtmEventTracking[GTMTracking.LoginPasswordRequirementError]
      );
    }
    if (this.credentialFieldHasRequiredError('userName')) {
      this.analyticsService.pushToDataLayer(
        gtmEventTracking[GTMTracking.LoginUsernameRequirementError]
      );
    }
    if (!this.loginForm.controls['userCredentials'].valid) return;

    this.isSubmitting = true;

    // get data
    const data: Credentials = {
      username:
        this.getCredentialsFormGroup().controls['userName'].value.trim(),
      password:
        this.getCredentialsFormGroup().controls['password'].value.trim(),
      tmxSessionId: this.threatMetrixService.getSessionId('Login'),
    };

    this.saveRememberMeCookie(data.username);

    this.errorService.clear();
    this.errors = [];

    this.authenticationService.getPasscodeChallenge(data).then(
      (result) => {
        that.challengeVerificationToken = result.challengeVerificationToken;
        that.passcodeInputs = that.getPasscodeArray(result.challenge);
        that.isSubmitting = false;
        this.loginError = false;

        that.gotoStep2();
      },
      (error) => {
        this.analyticsService.pushToDataLayer(
          gtmEventTracking[GTMTracking.LoginUsernamePasswordError]
        );
        this.errorService.processError(error);
        that.getCredentialsFormGroup().controls['password'].reset();
        that.getCredentialsFormGroup().controls['password'].markAsPristine();
        that.getCredentialsFormGroup().controls['password'].markAsUntouched();
        that.credentialsDirty = false;
        that.isSubmitting = false;
      }
    );
  }

  private saveRememberMeCookie(username: string) {
    if (!this.loginForm.controls['rememberMe'].value) {
      return;
    }

    this.cookieService.set(
      this.brand + 'userName',
      username,
      new Date('2099-01-01T00:00:00Z'),
      '/',
      undefined,
      true
    );
  }

  private getPasscodeArray(challenge: PasscodeChallenge): Array<boolean> {
    const arr = [];
    if (challenge.passcodeLength == null || challenge.passcodeLength == 0) {
      challenge.passcodeLength = 6;
    }

    // add blanks
    for (let i = 0; i < challenge.passcodeLength; i++) {
      arr.push(false);
    }

    // add inputs
    challenge.passcodeDigits.forEach((digit) => {
      arr[digit.position] = true;
    });

    return arr;
  }

  private submitStepTwo() {
    const that = this;

    this.loginError = false;
    this.passcodeComponent.submit();

    if (this.loginForm.invalid) {
      this.analyticsService.pushToDataLayer(
        gtmEventTracking[GTMTracking.LoginRequiredPasscodeError]
      );
      return;
    }

    this.isSubmitting = true;

    const response: PasscodeChallengeResponse = {
      passcodeDigits: this.loginForm.controls['passcode'].value,
      challengeVerificationToken: this.challengeVerificationToken,
      username:
        this.getCredentialsFormGroup().controls['userName'].value.trim(),
      password:
        this.getCredentialsFormGroup().controls['password'].value.trim(),
      rememberMe: this.loginForm.controls['rememberMe'].value,
      tmxSessionId: this.threatMetrixService.getSessionId('Login'),
    };

    if (this.loginForm.controls['rememberMe'].value) {
      this.cookieService.set(
        this.brand + 'userName',
        response.username,
        new Date('2099-01-01T00:00:00Z'),
        '/',
        undefined,
        true
      );
    }

    this.authenticationService.submitPasscodeChallenge(response).then(
      (result) => {
        if (result.success) {
          this.loginService.loginRedirect(this.returnUrl);
        }
      },
      (error: HttpErrorResponse) => {
        this.analyticsService.pushToDataLayer(
          gtmEventTracking[GTMTracking.LoginValidPasscodeError]
        );
        that.passcodeComponent.reset();
        that.isSubmitting = false;
        this.errorService.processError(error);
        if (error.status === 500) {
          this.loginError = true;
        }
      }
    );
  }

  private processErrorMessages() {
    const errors = [];

    if (this.credentialsMessage.srMessage) {
      errors.push(this.credentialsMessage.srMessage);
    }

    if (this.content) {
      if (this.credentialFieldHasError('userName')) {
        errors.push(this.content.form.usernameInput.requiredMessage);
      }

      if (this.credentialFieldHasError('password')) {
        errors.push(this.content.form.passwordInput.requiredMessage);
      }
    }

    if (this.passcodeComponent != null && !this.passcodeComponent.valid) {
      errors.push(this.errorMessages.passcode);
    }

    this.errors = errors;
  }
}
